import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import {
  addWorkspaceCreator,
  getCampaignFeed,
  getInvoiceDashboard,
  createExceptionLink,
  downloadInvoiceXlsx,
  downloadInvoiceZip,
  getExceptionLinks,
  getWorkspaceCampaignCreators,
  removeWorkspaceCreator,
  revokeExceptionLink,
  searchWorkspaceCampaigns,
  searchWorkspaceCreators,
  setShortlistPrice,
} from '../../api_handlers/invoice';
import { createCampaign } from '../../api_handlers/campaign';
import ProjectCommercials, { COMMERCIALS_CSS } from './ProjectCommercials';
import { spendOf } from './lib/commercials';
import {
  CREATOR,
  LINE_STATE_LABELS,
  VENDOR,
  isVendor,
  lineState,
  linePayload,
  validateLine,
} from './lib/rosterLine';
import { POST_PLATFORMS, hasMorePages, normalisePost, postsByLine } from './lib/campaignPosts';
import {
  lineComments,
  linePosts,
  lineProfiles,
  matchInvoice,
  profileHref,
  unpersisted,
} from './lib/lineDetails';
import { newCampaignPayload, validateNewCampaign } from './lib/newCampaign';
import {
  canRestore,
  isWithdrawn,
  removalOutcome,
  removalPlan,
  restorePayload,
} from './lib/removeCreator';
import { SPLIT_STATE_LABELS, lineStateWithSplits, splitSummary } from './lib/splits';
import { REASON_MAX, needsReason, pricePayload, validatePriceChange } from './lib/priceChange';
import { fieldLabel, historyOf } from './lib/priceHistory';
import NoticeStrip, { NOTICE_CSS } from './NoticeStrip';
import { AUDIENCE } from './lib/notifications';
import {
  needsOnboarding,
  onboardingLink,
  onboardingMessage,
  payoutState,
} from './lib/onboarding';
import { quickInvitePayloads, validateQuickInvite } from './lib/quickInvite';
import {
  Alert,
  Empty,
  ICONS,
  Icon,
  KIT_CSS,
  Modal,
  StatusPill,
  money,
  shortDate,
  useResponsiveViewport,
  useStyleSheet,
} from './kit';

/**
 * The invoicing workspace (IN-1718, Flow 1 + 2).
 *
 * Staff pick a campaign, add creators to it — from master data or new by
 * email — lock each creator's payout and the client billing, and, for the
 * exceptions, issue a no-sign-in invoice link. Per-campaign downloads live
 * here too, so one campaign's invoices come out as one set in one click.
 */

const CSS = `
.ws-layout { display: grid; grid-template-columns: 300px 1fr; gap: 18px; align-items: start; }
.ws-side { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 14px; position: sticky; top: 16px; }
.ws-list { margin-top: 10px; display: grid; gap: 4px; max-height: 70vh; overflow: auto; }
.ws-item { text-align: left; background: none; border: 1px solid transparent; border-radius: 10px; padding: 10px 11px; cursor: pointer; font: inherit; }
.ws-item:hover { background: var(--canvas); }
.ws-item--on { background: var(--accent-bg); border-color: var(--accent); }
.ws-item__name { font-size: 13.5px; font-weight: 800; color: var(--ink); margin: 0; }
.ws-item__meta { font-size: 12px; color: var(--ink-3); margin: 2px 0 0; }
.ws-main { display: grid; gap: 18px; min-width: 0; }
/* min-width:0 is load-bearing, not tidying. A grid child defaults to
   min-width:auto, so without it the card takes the table's min-content width,
   overflows its column, and .ws-scroll never scrolls -- the buttons in the
   last cell are clipped by the window instead. Only shows up with the planner
   drawer open, which is why it took five attempts to reproduce. */
.ws-card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 18px 20px; min-width: 0; }
.ws-card__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.ws-card__title { font-size: 16px; font-weight: 800; color: var(--ink); margin: 0; }
.ws-card__sub { font-size: 12.5px; color: var(--ink-3); margin: 3px 0 0; }
.ws-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.ws-seg { display: inline-flex; background: var(--canvas); border-radius: 10px; padding: 3px; margin-bottom: 14px; }
.ws-seg button { border: 0; background: none; font: inherit; font-size: 13px; font-weight: 700; color: var(--ink-3); padding: 6px 12px; border-radius: 8px; cursor: pointer; }
.ws-seg button.on { background: var(--surface); color: var(--ink); box-shadow: var(--shadow-sm); }
.ws-add { display: grid; grid-template-columns: 2fr 1fr 1fr auto auto; gap: 10px; align-items: end; }
.ws-add--new { grid-template-columns: 1.3fr 1.5fr 1fr 1fr auto auto; }
.ws-suggest { position: relative; }
.ws-suggest__list { position: absolute; z-index: 5; top: 100%; left: 0; right: 0; background: var(--surface); border: 1px solid var(--line); border-radius: 10px; box-shadow: var(--shadow-md); margin-top: 4px; max-height: 240px; overflow: auto; }
.ws-suggest__item { display: block; width: 100%; text-align: left; border: 0; background: none; font: inherit; padding: 9px 12px; cursor: pointer; }
.ws-suggest__item:hover { background: var(--canvas); }
.ws-lock { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--ink-2); white-space: nowrap; padding-bottom: 10px; }
.ws-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.ws-table th { text-align: left; font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); padding: 8px 10px; border-bottom: 1px solid var(--line); white-space: nowrap; }
.ws-table td { padding: 10px; border-bottom: 1px solid var(--line); vertical-align: middle; }
.ws-table tr:last-child td { border-bottom: 0; }
.ws-price { width: 110px; }
/* Sits under the Locked tag only while an agreed price is being moved. Bounded
   so a long note cannot widen the column and push the table sideways. */
.ws-reason { width: 160px; max-width: 100%; margin-top: 6px; font-size: 12px; }
.ws-name { font-weight: 800; color: var(--ink); }
.ws-sub { font-size: 12px; color: var(--ink-3); }
.ws-tag { display: inline-block; font-size: 10.5px; font-weight: 800; letter-spacing: .04em; border-radius: 999px; padding: 2px 8px; }
.ws-tag--ok { color: #15803D; background: #DCFCE7; }
.ws-tag--warn { color: #92400E; background: #FEF3C7; }
.ws-tag--grey { color: var(--ink-3); background: var(--canvas); }
.ws-url { display: flex; gap: 8px; align-items: center; margin-top: 12px; }
.ws-url input { flex: 1; font-family: ui-monospace, Menlo, monospace; font-size: 12.5px; }
.ws-scroll { overflow-x: auto; }

/* --- roster line: identity, posts, and the detail panel ------------- */
.ws-who { color: var(--ink); text-decoration: none; border-bottom: 1px solid var(--line-2); }
.ws-who:hover { color: var(--accent-ink, var(--ink)); border-bottom-color: currentColor; }
.ws-plats { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 4px; }
.ws-plat {
  font-size: 11px; font-weight: 700; color: var(--ink-3); background: var(--canvas);
  border: 1px solid var(--line); border-radius: 999px; padding: 1px 8px; text-decoration: none;
  max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ws-plat:hover { color: var(--ink); border-color: var(--line-2); }
.ws-posts { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; align-items: center; }
.ws-post {
  display: inline-flex; align-items: center; gap: 5px; max-width: 160px;
  font-size: 11px; font-weight: 700; color: var(--ink-2); background: var(--surface);
  border: 1px solid var(--line); border-radius: 999px; padding: 2px 9px; text-decoration: none;
}
.ws-post:hover { border-color: var(--line-2); background: var(--canvas); }
.ws-post__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ws-post__dot { flex: none; width: 6px; height: 6px; border-radius: 50%; background: var(--ink-4); }
.ws-post__dot--instagram { background: #C13584; }
.ws-post__dot--youtube { background: #FF0000; }
.ws-count {
  margin-left: 6px; font-size: 10.5px; font-weight: 800; min-width: 16px; text-align: center;
  color: var(--ink-3); background: var(--canvas); border-radius: 999px; padding: 0 5px;
}
.ws-tr--open > td { border-bottom: 0; background: var(--canvas); }
/* A withdrawn line stays on the roster -- its invoices are financial records
   and must stay readable -- but it is out of the campaign, so it reads as
   past rather than current. */
.ws-tr--withdrawn > td { opacity: 0.62; }
.ws-tr--withdrawn .ws-name { text-decoration: line-through; }
.ws-remove:not(:disabled) { color: var(--danger, #CC0000); }
.ws-tr--detail > td { padding: 0 10px 12px; background: var(--canvas); }
.ws-det { border: 1px solid var(--line); border-radius: var(--radius-sm); background: var(--surface); padding: 14px 16px; }
.ws-det__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.ws-det__title { font-size: 12px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-3); margin: 0; }
.ws-det__warn { font-size: 12.5px; color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: var(--radius-sm); padding: 8px 11px; margin: 0 0 12px; }
.ws-det__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 16px 20px; }
.ws-det__col { display: grid; gap: 10px; align-content: start; min-width: 0; }
.ws-det__label { font-size: 11px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-3); margin: 0; }
.ws-det__area { min-height: 72px; font-family: inherit; font-size: 13px; line-height: 1.5; resize: vertical; }
.ws-notes { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.ws-note__item { display: grid; gap: 2px; font-size: 12.5px; line-height: 1.5; color: var(--ink-2); border-left: 2px solid var(--line-2); padding-left: 10px; }
.ws-note__tag { font-size: 10.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: var(--ink-3); }
.ws-note__text { overflow-wrap: anywhere; }
.ws-plist { list-style: none; margin: 0; padding: 0; display: grid; gap: 7px; }
.ws-plist__item { display: flex; gap: 9px; align-items: flex-start; text-decoration: none; border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 7px 9px; }
.ws-plist__item:hover { border-color: var(--line-2); background: var(--canvas); }
.ws-plist__thumb { flex: none; width: 42px; height: 42px; border-radius: 8px; object-fit: cover; background: var(--canvas); border: 1px solid var(--line); }
.ws-plist__thumb--instagram { background: linear-gradient(135deg, #F9CE34 0%, #EE2A7B 55%, #6228D7 100%); }
.ws-plist__thumb--youtube { background: #FF0000; }
.ws-plist__body { display: grid; gap: 2px; min-width: 0; }
.ws-plist__top { font-size: 12px; font-weight: 800; color: var(--ink-2); }
.ws-plist__title { font-size: 12.5px; color: var(--ink-2); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.ws-kv { display: grid; grid-template-columns: auto 1fr; gap: 6px 12px; margin: 0; font-size: 12.5px; }
.ws-kv dt { color: var(--ink-3); font-weight: 700; white-space: nowrap; }
.ws-kv dd { margin: 0; color: var(--ink-2); overflow-wrap: anywhere; }
.ws-onboard { display: flex; align-items: center; gap: 6px; }
.ws-kind { margin-left: 7px; vertical-align: middle; }
.ws-attention { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 0 0 14px; }
.ws-attention__label { font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #92400E; }
.ws-attention__item { font-size: 12.5px; font-weight: 700; color: #92400E; background: #FEF3C7; border: 1px solid #FDE68A; border-radius: 999px; padding: 3px 11px; }
.ws-side__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.ws-side__title { font-size: 12px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); margin: 0; }
.ws-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.ws-note { font-size: 12.5px; line-height: 1.55; color: var(--ink-3); margin: 0 0 12px; }
.ws-msg { width: 100%; min-height: 150px; font-family: inherit; font-size: 12.5px; line-height: 1.55; }
@media (max-width: 960px) { .ws-layout { grid-template-columns: 1fr; } .ws-side { position: static; } .ws-add, .ws-add--new { grid-template-columns: 1fr 1fr; } }
`;

/**
 * Every post on one campaign, both platforms, normalised.
 *
 * Capped at five pages a platform: the feed returns ten rows a page, and fifty
 * posts per platform is already past the point where a roster line is reading
 * them one by one. A platform that fails is skipped rather than failing the
 * page — posts are context on the line, not the line itself.
 */
async function loadCampaignPosts(campaignId) {
  const perPlatform = await Promise.all(
    POST_PLATFORMS.map(async (platform) => {
      const posts = [];
      let fetched = 0;
      for (let page = 1; page <= MAX_FEED_PAGES; page += 1) {
        let body = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          body = await getCampaignFeed(campaignId, platform, page);
        } catch (e) {
          break;
        }
        const rows = body.results || [];
        fetched += rows.length;
        rows.forEach((row) => {
          const post = normalisePost(row, platform);
          if (post) posts.push(post);
        });
        if (!hasMorePages(body, fetched)) break;
      }
      return posts;
    }),
  );
  return perPlatform.flat();
}

function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const MAX_FEED_PAGES = 5;

function linkState(link) {
  if (link.revoked_at) return { label: 'Revoked', tone: 'grey' };
  if (!link.is_active) return { label: 'Expired', tone: 'grey' };
  if (link.invoice_id) return { label: 'Used', tone: 'ok' };
  return { label: 'Active', tone: 'warn' };
}

async function copy(text, what = 'Link') {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch (e) {
    toast.error('Copy failed — select the text and copy it by hand');
  }
}

/**
 * Flow 1 step 1: set the project up here, rather than having to create it
 * elsewhere first and come back. Uses the brand campaign endpoint the planner
 * already posts to, tagged so ops can see where the campaign came from.
 */
function NewCampaignModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ name: '', start_date: '', end_date: '' });
    setErrors({});
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    const found = validateNewCampaign(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      // This endpoint returns the raw response, unlike the invoice handlers.
      const response = await createCampaign(newCampaignPayload(form), 'Invoicing › Create campaign');
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || body.detail || 'Could not create the campaign.');
      }
      const campaign = await response.json();
      toast.success('Campaign created');
      onCreated(campaign);
      onClose();
    } catch (e) {
      setErrors({ form: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New campaign"
      subtitle="Set the project up, then add its creators"
      footer={
        <>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <span className="iv-spin" /> : null}
            Create campaign
          </button>
        </>
      }
    >
      {errors.form ? <Alert>{errors.form}</Alert> : null}
      <label className="iv-field">
        <span className="iv-label">Campaign name</span>
        <input
          className="iv-input"
          value={form.name}
          onChange={set('name')}
          placeholder="e.g. Kurlon Onam 2026"
        />
        {errors.name ? <span className="iv-error">{errors.name}</span> : null}
      </label>
      <div className="ws-dates">
        <label className="iv-field">
          <span className="iv-label">Starts</span>
          <input className="iv-input" type="date" value={form.start_date} onChange={set('start_date')} />
          {errors.start_date ? <span className="iv-error">{errors.start_date}</span> : null}
        </label>
        <label className="iv-field">
          <span className="iv-label">Ends</span>
          <input className="iv-input" type="date" value={form.end_date} onChange={set('end_date')} />
          {errors.end_date ? <span className="iv-error">{errors.end_date}</span> : null}
        </label>
      </div>
      <p className="ws-note" style={{ marginTop: 12, marginBottom: 0 }}>
        Dates are optional, but the invoice console filters on them — set both or neither.
      </p>
    </Modal>
  );
}

function CampaignPicker({ selectedId, onSelect, refreshKey, onNew, autoSelectId }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const search = useDebounced(query);
  // Only ever honoured once, so later searches do not yank the selection back.
  const autoSelected = useRef(false);

  useEffect(() => {
    let alive = true;
    searchWorkspaceCampaigns(search)
      .then((data) => {
        if (!alive) return;
        const list = data || [];
        setItems(list);
        setError('');
        if (autoSelectId && !autoSelected.current) {
          const match = list.find((c) => String(c.id) === String(autoSelectId));
          if (match) {
            autoSelected.current = true;
            onSelect(match);
          }
        }
      })
      .catch((e) => {
        if (!alive) return;
        setItems([]);
        // A 403 here means the viewer is not on the business team.
        setError(e.message);
      });
    return () => {
      alive = false;
    };
    // autoSelectId / onSelect are deliberately out: the ref guard above makes
    // this a one-shot, and onSelect changes identity on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, refreshKey]);

  return (
    <aside className="ws-side">
      <div className="ws-side__head">
        <p className="ws-side__title">Campaigns</p>
        <button type="button" className="iv-btn iv-btn--ghost iv-btn--sm" onClick={onNew}>
          <Icon path={ICONS.plus} size={14} />
          New
        </button>
      </div>
      <div className="iv-search">
        <span className="iv-search__icon">
          <Icon path={ICONS.search} size={15} />
        </span>
        <input
          className="iv-input"
          placeholder="Find a campaign or brand"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search campaigns"
        />
      </div>
      <div className="ws-list">
        {items.map((campaign) => (
          <button
            key={campaign.id}
            type="button"
            className={`ws-item${String(campaign.id) === String(selectedId) ? ' ws-item--on' : ''}`}
            onClick={() => onSelect(campaign)}
          >
            <p className="ws-item__name">{campaign.name}</p>
            <p className="ws-item__meta">
              {campaign.brand_name ? `${campaign.brand_name} · ` : ''}
              {campaign.campaign_subtype || 'campaign'} · {campaign.status || 'draft'}
            </p>
          </button>
        ))}
        {error ? <Alert>{error}</Alert> : null}
        {!error && items.length === 0 ? <p className="ws-sub">No campaigns match.</p> : null}
      </div>
    </aside>
  );
}

function AddCreator({ campaignId, onAdded }) {
  const [mode, setMode] = useState('existing');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    vendor_name: '',
    deliverables: '',
    managed_by: 'direct',
    influencer_final_price: '',
    final_price: '',
    locked: true,
  });
  const isVendorMode = mode === 'vendor';
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const search = useDebounced(query);

  useEffect(() => {
    if (mode !== 'existing' || picked || search.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    let alive = true;
    searchWorkspaceCreators(search)
      .then((data) => alive && setSuggestions(data || []))
      .catch(() => alive && setSuggestions([]));
    return () => {
      alive = false;
    };
  }, [search, mode, picked]);

  const set = (key) => (e) =>
    setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async () => {
    // One shape for both line kinds — see lib/rosterLine.
    const line = {
      kind: isVendorMode ? VENDOR : CREATOR,
      influencer_id: !isVendorMode && mode === 'existing' && picked ? picked.id : null,
      name: form.name,
      email: form.email,
      vendor_name: form.vendor_name,
      deliverables: form.deliverables,
      managed_by: form.managed_by,
      internal_cost: form.influencer_final_price,
      client_cost: form.final_price,
      locked: form.locked,
    };
    const found = validateLine(line);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      await addWorkspaceCreator(campaignId, linePayload(line));
      toast.success(isVendorMode ? 'Vendor added' : 'Creator added');
      setPicked(null);
      setQuery('');
      setForm({
        name: '',
        email: '',
        vendor_name: '',
        deliverables: '',
        managed_by: 'direct',
        influencer_final_price: '',
        final_price: '',
        locked: true,
      });
      onAdded();
    } catch (e) {
      setErrors(e.fieldErrors || {});
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const err = (key) => (errors[key] ? <span className="iv-hint iv-hint--error">{String(errors[key])}</span> : null);

  return (
    <div className="ws-card">
      <div className="ws-card__head">
        <div>
          <p className="ws-card__title">Add to this project</p>
          <p className="ws-card__sub">
            {isVendorMode
              ? 'Vendor lines are not supported by the API yet. Add the editor, boosting or shoot cost as a new creator with a name and email — it is paid through the same run.'
              : 'From master data, or someone new — they get an account on this email and land on it when they sign in with Google.'}
          </p>
        </div>
      </div>
      <div className="ws-seg" role="tablist">
        <button type="button" className={mode === 'existing' ? 'on' : ''} onClick={() => setMode('existing')}>
          Existing creator
        </button>
        <button type="button" className={mode === 'new' ? 'on' : ''} onClick={() => setMode('new')}>
          New creator
        </button>
        {/* Disabled, not removed. `POST .../creators` needs an influencer_id
            or a name and email, and the vendor payload carries neither, so
            every vendor add returns 400 with "Enter the creator's name" --
            a message about a field the form does not show. Nothing is saved,
            so there is no bad data; there is just a button that cannot work.
            The backend has no vendor concept at all yet (IN-1821, 29 Oct). */}
        <button
          type="button"
          className=""
          disabled
          title="Vendor lines are not supported by the API yet. Add the editor or vendor as a new creator with their name and email; they are paid through the same run."
        >
          Vendor
        </button>
      </div>
      <div className={`ws-add${mode !== 'existing' ? ' ws-add--new' : ''}`}>
        {isVendorMode ? (
          <label className="iv-field">
            <span className="iv-label">Vendor name</span>
            <input
              className="iv-input"
              value={form.vendor_name}
              placeholder="e.g. Chirag (editor), Meta boosting"
              onChange={set('vendor_name')}
            />
            {err('vendor_name')}
          </label>
        ) : mode === 'existing' ? (
          <label className="iv-field ws-suggest">
            <span className="iv-label">Creator</span>
            <input
              className="iv-input"
              placeholder="Name, email or Instagram handle"
              value={picked ? `${picked.name} (${picked.email})` : query}
              onChange={(e) => {
                setPicked(null);
                setQuery(e.target.value);
              }}
            />
            {suggestions.length ? (
              <div className="ws-suggest__list">
                {suggestions.map((each) => (
                  <button
                    key={each.id}
                    type="button"
                    className="ws-suggest__item"
                    onClick={() => {
                      setPicked(each);
                      setSuggestions([]);
                    }}
                  >
                    <span className="ws-name">{each.name || 'Unnamed'}</span>{' '}
                    <span className="ws-sub">{each.email}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {err('influencer_id')}
          </label>
        ) : (
          <>
            <label className="iv-field">
              <span className="iv-label">Name</span>
              <input className="iv-input" value={form.name} onChange={set('name')} />
              {err('name')}
            </label>
            <label className="iv-field">
              <span className="iv-label">Email</span>
              <input className="iv-input" type="email" value={form.email} onChange={set('email')} />
              {err('email')}
            </label>
          </>
        )}
        <label className="iv-field">
          <span className="iv-label">Deliverables</span>
          <input
            className="iv-input"
            value={form.deliverables}
            placeholder={isVendorMode ? '7 Video Edits' : '1 Non-Collab Reel'}
            onChange={set('deliverables')}
          />
          {err('deliverables')}
        </label>
        {isVendorMode ? null : (
          <label className="iv-field">
            <span className="iv-label">Managed by</span>
            <select className="iv-input" value={form.managed_by} onChange={set('managed_by')}>
              <option value="direct">Direct</option>
              <option value="agency">Agency</option>
            </select>
          </label>
        )}
        <label className="iv-field">
          <span className="iv-label">{isVendorMode ? 'Vendor cost (₹)' : 'Creator payout (₹)'}</span>
          <input
            className="iv-input iv-num"
            type="number"
            min="0"
            value={form.influencer_final_price}
            onChange={set('influencer_final_price')}
          />
          {err('internal_cost')}
          {err('influencer_final_price')}
        </label>
        <label className="iv-field">
          <span className="iv-label">Client billing (₹)</span>
          <input
            className="iv-input iv-num"
            type="number"
            min="0"
            value={form.final_price}
            onChange={set('final_price')}
          />
          {err('final_price')}
        </label>
        <label className="ws-lock">
          <input type="checkbox" checked={form.locked} onChange={set('locked')} />
          Lock price
        </label>
        <button type="button" className="iv-btn iv-btn--primary" onClick={submit} disabled={saving}>
          {saving ? <span className="iv-spin" /> : <Icon path={ICONS.plus} size={14} />}
          Add
        </button>
      </div>
    </div>
  );
}

const PLATFORM_LABEL = { instagram: 'Instagram', youtube: 'YouTube', other: 'Link' };

/** What the detail panel edits. Prices stay on the row, where they are scanned. */
const DETAIL_FIELDS = ['deliverables', 'managed_by', 'internal_poc', 'notes'];

const DETAIL_LABELS = {
  deliverables: 'Deliverables',
  managed_by: 'Managed by',
  internal_poc: 'Internal POC',
  notes: 'Comments',
};

function detailFormOf(row) {
  return DETAIL_FIELDS.reduce(
    (form, field) => ({ ...form, [field]: row[field] === null || row[field] === undefined ? '' : String(row[field]) }),
    {},
  );
}

const compact = (value) =>
  value >= 100000
    ? `${(value / 100000).toFixed(value >= 1000000 ? 0 : 1)}L`
    : value >= 1000
    ? `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}K`
    : String(value);

/** "1.5L reach · 5K likes" — only the figures the feed actually returned. */
function postFigures(post) {
  const metrics = post.metrics || {};
  return [
    metrics.reach ? `${compact(metrics.reach)} reach` : '',
    metrics.views ? `${compact(metrics.views)} views` : '',
    metrics.likes ? `${compact(metrics.likes)} likes` : '',
    metrics.comments ? `${compact(metrics.comments)} comments` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Post links, which are the proof the deliverable happened. */
function PostLinks({ posts, limit }) {
  const shown = limit ? posts.slice(0, limit) : posts;
  const rest = posts.length - shown.length;
  if (posts.length === 0) return null;
  return (
    <div className="ws-posts">
      {shown.map((post) => (
        <a
          key={post.url}
          className="ws-post"
          href={post.url}
          target="_blank"
          rel="noopener noreferrer"
          title={`${PLATFORM_LABEL[post.platform] || post.platform} · ${post.label || post.url}`}
        >
          <span className={`ws-post__dot ws-post__dot--${post.platform}`} />
          <span className="ws-post__label">{post.label || 'Post'}</span>
        </a>
      ))}
      {rest > 0 ? <span className="ws-sub">+{rest} more</span> : null}
    </div>
  );
}

/** The same posts with what the campaign feed knows about each one. */
function PostList({ posts }) {
  return (
    <ul className="ws-plist">
      {posts.map((post) => {
        const figures = postFigures(post);
        return (
          <li key={post.url}>
            <a className="ws-plist__item" href={post.url} target="_blank" rel="noopener noreferrer">
              {post.thumbnail ? (
                <img className="ws-plist__thumb" src={post.thumbnail} alt="" loading="lazy" />
              ) : (
                <span className={`ws-plist__thumb ws-plist__thumb--${post.platform}`} aria-hidden="true" />
              )}
              <span className="ws-plist__body">
                <span className="ws-plist__top">
                  {PLATFORM_LABEL[post.platform] || post.platform} · {post.label || 'Post'}
                  {post.at ? <span className="ws-sub"> · {post.at}</span> : null}
                </span>
                {post.title ? <span className="ws-plist__title">{post.title}</span> : null}
                {figures ? <span className="ws-sub">{figures}</span> : null}
              </span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The sheet's remaining columns, one click away: what was agreed, what was
 * posted, what anyone has said about the line, and the invoice against it.
 *
 * Three columns rather than a stack — the row is already wide, and a staff
 * member reading comments wants the invoice beside them, not below the fold.
 */
function LineDetails({ row, invoice, invoices, link, posts, onSaved }) {
  const splits = splitSummary(row, invoices);
  const history = historyOf(row);
  const vendor = isVendor(row);
  const [form, setForm] = useState(() => detailFormOf(row));
  const [saving, setSaving] = useState(false);
  const [dropped, setDropped] = useState([]);
  const pending = useRef(null);

  useEffect(() => {
    setForm(detailFormOf(row));
    // Several of these fields do not exist in the API yet. Compare what came
    // back with what was sent, so a note that was silently discarded says so
    // instead of looking saved.
    if (pending.current) {
      setDropped(unpersisted(pending.current, row));
      pending.current = null;
    }
  }, [row]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const comments = lineComments(row, { invoice, link });
  const saved = detailFormOf(row);
  const dirty = DETAIL_FIELDS.some((field) => form[field] !== saved[field]);

  const save = async () => {
    const payload = DETAIL_FIELDS.reduce((body, field) => {
      // An enum cannot be sent empty; leaving it out keeps the server default.
      if (field === 'managed_by' && !form[field]) return body;
      return { ...body, [field]: form[field] };
    }, {});
    setSaving(true);
    setDropped([]);
    try {
      // The line endpoint wants the prices with it, so send the ones already
      // on the row rather than clearing them.
      await setShortlistPrice(row.id, {
        influencer_final_price: row.influencer_final_price,
        final_price: row.final_price,
        ...payload,
      });
      pending.current = payload;
      toast.success('Line details saved');
      onSaved();
    } catch (e) {
      toast.error((e.fieldErrors && Object.values(e.fieldErrors)[0]) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ws-det">
      <div className="ws-det__head">
        <p className="ws-det__title">Line details</p>
        <div className="ws-actions">
          <button
            type="button"
            className="iv-btn iv-btn--primary iv-btn--sm"
            onClick={save}
            disabled={saving || !dirty}
          >
            {saving ? <span className="iv-spin" /> : null}
            Save details
          </button>
        </div>
      </div>

      {dropped.length > 0 ? (
        <p className="ws-det__warn">
          Saved the price, but the API did not keep{' '}
          {dropped.map((field) => (DETAIL_LABELS[field] || field).toLowerCase()).join(', ')} — those
          roster fields are not live yet.
        </p>
      ) : null}

      <div className="ws-det__grid">
        <div className="ws-det__col">
          <p className="ws-det__label">What was agreed</p>
          <label className="iv-field">
            <span className="iv-label">Deliverables</span>
            <input
              className="iv-input"
              value={form.deliverables}
              placeholder={vendor ? '7 Video Edits' : '1 Non-Collab Reel'}
              onChange={set('deliverables')}
            />
          </label>
          {vendor ? null : (
            <label className="iv-field">
              <span className="iv-label">Managed by</span>
              <select className="iv-input" value={form.managed_by || 'direct'} onChange={set('managed_by')}>
                <option value="direct">Direct</option>
                <option value="agency">Agency</option>
              </select>
            </label>
          )}
          <label className="iv-field">
            <span className="iv-label">Internal POC</span>
            <input
              className="iv-input"
              value={form.internal_poc}
              placeholder="Who chases this line"
              onChange={set('internal_poc')}
            />
          </label>

          {history.length > 0 ? (
            <>
              <p className="ws-det__label">Price history</p>
              <ul className="ws-notes">
                {history.map((change, index) => (
                  <li
                    className="ws-note__item"
                    key={`${change.field}-${change.changed_at || index}`}
                  >
                    <span className="ws-note__text">
                      {fieldLabel(change.field)}{' '}
                      <span className="iv-num">
                        {change.old_value === null || change.old_value === undefined
                          ? 'not set'
                          : money(change.old_value)}
                      </span>
                      {' to '}
                      <span className="iv-num">{money(change.new_value)}</span>
                    </span>
                    {change.reason ? (
                      <span className="ws-note__text">{change.reason}</span>
                    ) : (
                      // Blank rather than dressed up: the import and the admin
                      // edit leave no reason, and that is the thing to notice.
                      <span className="ws-sub">No reason recorded</span>
                    )}
                    <span className="ws-sub">
                      {[
                        change.changed_by || 'Unknown',
                        change.changed_at ? shortDate(change.changed_at) : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>

        <div className="ws-det__col">
          <p className="ws-det__label">Comments</p>
          {comments.length > 0 ? (
            <ul className="ws-notes">
              {comments.map((comment) => (
                <li className="ws-note__item" key={comment.id}>
                  <span className="ws-note__tag">{comment.label}</span>
                  <span className="ws-note__text">{comment.text}</span>
                  {comment.by || comment.at ? (
                    <span className="ws-sub">
                      {[comment.by, comment.at ? shortDate(comment.at) : ''].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          <label className="iv-field">
            <span className="iv-label">Add a comment</span>
            <textarea
              className="iv-input ws-det__area"
              value={form.notes}
              placeholder="Anything the next person needs to know about this line"
              onChange={set('notes')}
            />
          </label>
        </div>

        <div className="ws-det__col">
          <p className="ws-det__label">Delivery and invoice</p>
          {posts.length > 0 ? (
            <PostList posts={posts} />
          ) : (
            <p className="ws-sub">
              No posts on this campaign matched to this line yet.
            </p>
          )}
          <dl className="ws-kv">
            <dt>Line state</dt>
            <dd>
              {(() => {
                const state = lineStateWithSplits(row, invoices);
                return LINE_STATE_LABELS[state] || SPLIT_STATE_LABELS[state];
              })()}
            </dd>
            {splits.count > 0 && splits.agreed > 0 ? (
              <>
                <dt>Invoiced</dt>
                <dd>
                  <span className="iv-num">{money(splits.invoiced)}</span> of{' '}
                  <span className="iv-num">{money(splits.agreed)}</span>
                  {splits.count > 1 ? ` across ${splits.count} invoices` : ''}
                  {splits.over ? ` — over by ${money(splits.overBy)}` : ''}
                </dd>
              </>
            ) : null}
            {splits.repriced ? (
              <>
                <dt>Repriced</dt>
                <dd>
                  {splits.repricedCount === 1
                    ? 'One invoice was raised against a different agreed price.'
                    : `${splits.repricedCount} invoices were raised against a different agreed price.`}
                </dd>
              </>
            ) : null}
            {splits.paid > 0 && splits.outstanding > 0 ? (
              <>
                <dt>Paid</dt>
                <dd>
                  <span className="iv-num">{money(splits.paid)}</span>, with{' '}
                  <span className="iv-num">{money(splits.outstanding)}</span> still outstanding
                </dd>
              </>
            ) : null}
            {row.price_locked ? (
              <>
                <dt>Price locked by</dt>
                <dd>{row.price_locked_by || 'staff'}</dd>
              </>
            ) : null}
            {invoice ? (
              <>
                <dt>Invoice</dt>
                <dd>
                  {invoice.invoice_url ? (
                    <a href={invoice.invoice_url} target="_blank" rel="noopener noreferrer">
                      {invoice.invoice_number || 'View document'}
                    </a>
                  ) : (
                    invoice.invoice_number || '—'
                  )}
                </dd>
                <dt>Invoiced</dt>
                <dd className="iv-num">{money(invoice.amount)}</dd>
                <dt>Submitted</dt>
                <dd>{shortDate(invoice.submitted_at)}</dd>
              </>
            ) : (
              <>
                <dt>Invoice</dt>
                <dd className="ws-sub">Not raised</dd>
              </>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}

function CreatorRow({
  row,
  invoice,
  campaignInvoices,
  link,
  campaignPosts,
  onSaved,
  onIssueLink,
  onInvite,
  onRemove,
  onRestore,
  restoring,
}) {
  const vendor = isVendor(row);
  const state = lineState(row);
  const removal = removalPlan(row);
  const withdrawn = isWithdrawn(row);
  const payoutRec = payoutState(row);
  const [payout, setPayout] = useState(String(row.influencer_final_price || ''));
  const [billing, setBilling] = useState(String(row.final_price || ''));
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    setPayout(String(row.influencer_final_price || ''));
    setBilling(String(row.final_price || ''));
    // A reason belongs to the change that was typed, not to the line.
    setReason('');
  }, [row.influencer_final_price, row.final_price]);

  const dirty =
    Number(payout || 0) !== Number(row.influencer_final_price || 0) ||
    Number(billing || 0) !== Number(row.final_price || 0);

  const next = { payout, billing };
  const mustExplain = needsReason(row, next);

  const save = async (extra = {}) => {
    const found = validatePriceChange(row, next, reason);
    if (found.reason) {
      toast.error(found.reason);
      return;
    }

    setSaving(true);
    try {
      await setShortlistPrice(row.id, pricePayload(row, next, { reason, extra }));
      setReason('');
      onSaved();
    } catch (e) {
      const fields = e.fieldErrors || {};
      toast.error(fields.influencer_final_price || fields.final_price || e.message);
    } finally {
      setSaving(false);
    }
  };

  const profiles = lineProfiles(row, invoice);
  const posts = linePosts(row, invoice, campaignPosts);
  const commentCount = lineComments(row, { invoice, link }).length;
  const primary = profiles[0] || null;
  const name = vendor ? row.vendor_name || 'Unnamed vendor' : row.influencer_name || 'Unnamed creator';
  const primaryHref = profileHref(primary);

  return (
    <>
      <tr className={[open ? 'ws-tr--open' : '', withdrawn ? 'ws-tr--withdrawn' : '']
        .filter(Boolean).join(' ') || undefined}
      >
        <td>
          <div className="ws-name">
            {primaryHref ? (
              <a
                className="ws-who"
                href={primaryHref}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open ${name} in the planner`}
              >
                {name}
              </a>
            ) : (
              name
            )}
            {withdrawn ? (
              <span className="ws-tag ws-tag--warn ws-kind" title="Withdrawn from this campaign. Its invoices are kept.">
                Withdrawn
              </span>
            ) : null}
            {vendor ? <span className="ws-tag ws-tag--grey ws-kind">Vendor</span> : null}
            {!vendor && row.managed_by === 'agency' ? (
              <span className="ws-tag ws-tag--grey ws-kind">Agency</span>
            ) : null}
          </div>
          {profiles.length > 0 ? (
            <div className="ws-plats">
              {profiles.map((profile) => (
                <a
                  key={`${profile.platform}-${profile.handle}`}
                  className="ws-plat"
                  href={profileHref(profile)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open ${profile.handle} on ${PLATFORM_LABEL[profile.platform] || profile.platform}`}
                >
                  {PLATFORM_LABEL[profile.platform] || profile.platform} · @{profile.handle}
                </a>
              ))}
            </div>
          ) : null}
          <div className="ws-sub">
            {row.deliverables ? `${row.deliverables} · ` : ''}
            {vendor ? LINE_STATE_LABELS[state] : row.email || LINE_STATE_LABELS[state]}
          </div>
          {row.internal_poc ? <div className="ws-sub">POC {row.internal_poc}</div> : null}
          <PostLinks posts={posts} limit={2} />
        </td>
        <td>
          <input
            className="iv-input iv-num ws-price"
            type="number"
            min="0"
            value={payout}
            onChange={(e) => setPayout(e.target.value)}
            aria-label="Creator payout"
          />
        </td>
        <td>
          <input
            className="iv-input iv-num ws-price"
            type="number"
            min="0"
            value={billing}
            onChange={(e) => setBilling(e.target.value)}
            aria-label="Client billing"
          />
        </td>
        <td>
          {row.price_locked ? (
            <span className="ws-tag ws-tag--ok" title={`Locked by ${row.price_locked_by || 'staff'}`}>
              Locked
            </span>
          ) : (
            <span className="ws-tag ws-tag--grey">Open</span>
          )}
          {mustExplain ? (
            <input
              className="iv-input ws-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is it changing?"
              maxLength={REASON_MAX}
              aria-label="Reason for the price change"
            />
          ) : null}
        </td>
        <td>
          {vendor ? (
            <span className="ws-sub">Not required</span>
          ) : payoutRec.tone === 'ok' ? (
            <span
              className="ws-tag ws-tag--ok"
              title={
                payoutRec.state === 'ready'
                  ? 'Bank, PAN and Udyam where needed are all on file'
                  : 'A payout record exists'
              }
            >
              {payoutRec.label}
            </span>
          ) : (
            <div className="ws-onboard">
              <span
                className="ws-tag ws-tag--warn"
                title={
                  payoutRec.state === 'incomplete'
                    ? 'A payout record was started but is missing bank, PAN or the Udyam number. A payment run will reject their invoice.'
                    : 'No payout record. They cannot raise an invoice.'
                }
              >
                {payoutRec.label}
              </span>
              {needsOnboarding(row) || payoutRec.state === 'incomplete' ? (
                <button
                  type="button"
                  className="iv-btn iv-btn--ghost iv-btn--sm"
                  onClick={() => onInvite(row)}
                  title="Send this creator the link to sign in and finish their payout details"
                >
                  {payoutRec.state === 'incomplete' ? 'Remind' : 'Invite'}
                </button>
              ) : null}
            </div>
          )}
        </td>
        <td>
          {row.invoice_status ? (
            <StatusPill status={row.invoice_status} />
          ) : (
            <span className="ws-sub">{row.block_reason ? 'Not invoiceable' : 'Not raised'}</span>
          )}
        </td>
        <td>
          <div className="ws-actions" style={{ justifyContent: 'flex-end' }}>
            {dirty ? (
              <button type="button" className="iv-btn iv-btn--primary iv-btn--sm" disabled={saving} onClick={() => save()}>
                Save
              </button>
            ) : null}
            <button
              type="button"
              className="iv-btn iv-btn--ghost iv-btn--sm"
              onClick={() => setOpen((was) => !was)}
              aria-expanded={open}
              title="Deliverables, comments, posts and the invoice on this line"
            >
              {open ? 'Hide' : 'Details'}
              {commentCount > 0 && !open ? <span className="ws-count">{commentCount}</span> : null}
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--ghost iv-btn--sm"
              disabled={saving}
              onClick={() => save({ locked: !row.price_locked })}
            >
              {row.price_locked ? 'Unlock' : 'Lock'}
            </button>
            <button
              type="button"
              className="iv-btn iv-btn--ghost iv-btn--sm"
              disabled={Boolean(row.block_reason)}
              title={row.block_reason || 'Invoice link without sign-in'}
              onClick={() => onIssueLink(row)}
            >
              Exception link
            </button>
            {withdrawn ? (
              <button
                type="button"
                className="iv-btn iv-btn--ghost iv-btn--sm"
                disabled={saving || restoring || !canRestore(row)}
                title={
                  canRestore(row)
                    ? 'Put this line back on the campaign. Its prices and invoices are unchanged, and its invoices become payable again.'
                    : 'This line has no creator to re-add. Add it as a new line.'
                }
                onClick={() => onRestore(row)}
              >
                Add back
              </button>
            ) : (
              <button
                type="button"
                className="iv-btn iv-btn--ghost iv-btn--sm ws-remove"
                disabled={removal.action === 'refuse'}
                title={removal.body}
                onClick={() => onRemove(row)}
              >
                {removal.confirmLabel}
              </button>
            )}
          </div>
        </td>
      </tr>
      {open ? (
        <tr className="ws-tr--detail">
          <td colSpan={7}>
            <LineDetails
              row={row}
              invoice={invoice}
              invoices={campaignInvoices}
              link={link}
              posts={posts}
              onSaved={onSaved}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Flow 1 step 3: get a creator who was added by name and email to actually
 * onboard. There is no send-email endpoint, so this hands staff the link and a
 * message to paste wherever they already talk to the creator.
 */
/**
 * Removing a line is the one roster action that cannot be undone from the
 * screen, so it asks first and says which of the two things it will do. The
 * server decides; this only sets the expectation.
 */
function RemoveLineModal({ row, campaignId, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const plan = removalPlan(row);

  async function confirm() {
    setBusy(true);
    try {
      const body = await removeWorkspaceCreator(campaignId, row.id);
      const result = removalOutcome(body);
      toast.success(result.message);
      onDone();
      onClose();
    } catch (e) {
      // The paid case comes back as a 400 whose `detail` parseError has
      // already folded into `message`, so the server's own wording reaches
      // staff rather than a generic line.
      toast.error((e && e.message) || 'Could not remove the line.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(row)}
      onClose={onClose}
      tone="danger"
      title={plan.title}
      subtitle="This cannot be undone from here."
      footer={
        <>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="iv-btn iv-btn--primary"
            onClick={confirm}
            disabled={busy || plan.action === 'refuse'}
          >
            {busy ? 'Working…' : plan.confirmLabel}
          </button>
        </>
      }
    >
      <p className="ws-note">{plan.body}</p>
      {plan.action === 'remove' ? (
        <p className="ws-note">
          Adding them again later restores the line.
        </p>
      ) : null}
    </Modal>
  );
}

function InviteModal({ row, campaign, onClose }) {
  const link = onboardingLink(typeof window === 'undefined' ? '' : window.location.origin);
  const message = onboardingMessage(
    { ...(row || {}), campaign_name: campaign ? campaign.name : '' },
    link,
  );

  return (
    <Modal
      open={Boolean(row)}
      onClose={onClose}
      title="Invite to onboard"
      subtitle={row ? `${row.influencer_name} — sign in and add payout details` : ''}
      footer={
        <>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="iv-btn iv-btn--primary"
            onClick={() => copy(message, 'Message')}
          >
            Copy message
          </button>
        </>
      }
    >
      <p className="ws-note">
        They sign in with Instagram or YouTube, then add bank, PAN, GST and MSME details once.
        Until that is done they cannot raise an invoice.
      </p>
      <div className="ws-url">
        <input className="iv-input" readOnly value={link} aria-label="Onboarding link" />
        <button type="button" className="iv-btn iv-btn--ghost" onClick={() => copy(link)}>
          Copy
        </button>
      </div>
      <textarea className="iv-input ws-msg" readOnly value={message} aria-label="Invite message" />
    </Modal>
  );
}

/**
 * Flow 2, the urgent case: add the creator and issue their no-sign-in link in
 * one submit, rather than adding them, pricing them and then issuing a link.
 */
function QuickInviteModal({ open, campaign, onClose, onDone }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    influencer_final_price: '',
    final_price: '',
    reason: '',
    expires_in_days: '7',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: '',
      email: '',
      influencer_final_price: '',
      final_price: '',
      reason: '',
      expires_in_days: '7',
    });
    setErrors({});
    setIssued(null);
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    const found = validateQuickInvite(form, null);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      const { creator, link } = quickInvitePayloads(form, null);
      const added = await addWorkspaceCreator(campaign.id, creator);
      // The add returns the shortlist row the link has to be issued against.
      const shortlistId = added && (added.id || added.shortlisted_influencer_id);
      if (!shortlistId) {
        throw new Error('Creator was added, but no shortlist id came back — issue the link from their row.');
      }
      const result = await createExceptionLink({ shortlisted_influencer_id: shortlistId, ...link });
      setIssued(result);
      onDone();
    } catch (e) {
      const fields = e.fieldErrors || {};
      setErrors({ form: fields.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Urgent invoice link"
      subtitle={campaign ? `${campaign.name} — add the creator and issue their link in one step` : ''}
      footer={
        issued ? (
          <button type="button" className="iv-btn iv-btn--primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="iv-btn iv-btn--primary" onClick={submit} disabled={saving}>
              {saving ? <span className="iv-spin" /> : null}
              Add and issue link
            </button>
          </>
        )
      }
    >
      {issued ? (
        <>
          <p className="ws-note">
            Send this to whoever is filling the form. It expires in {form.expires_in_days} day(s)
            and can be revoked from the creator&apos;s row.
          </p>
          <div className="ws-url">
            <input className="iv-input" readOnly value={issued.url || issued.link || ''} aria-label="Exception link" />
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              onClick={() => copy(issued.url || issued.link || '')}
            >
              Copy
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="ws-note">
            Use this only when the creator cannot sign in — an urgent payment, or an agency that
            will not authenticate. Every exception shows up in the usage report.
          </p>
          {errors.form ? <Alert>{errors.form}</Alert> : null}
          <div className="ws-add ws-add--new">
            <label className="iv-field">
              <span className="iv-label">Creator name</span>
              <input className="iv-input" value={form.name} onChange={set('name')} />
              {errors.name ? <span className="iv-error">{errors.name}</span> : null}
            </label>
            <label className="iv-field">
              <span className="iv-label">Creator email</span>
              <input className="iv-input" type="email" value={form.email} onChange={set('email')} />
              {errors.email ? <span className="iv-error">{errors.email}</span> : null}
            </label>
            <label className="iv-field">
              <span className="iv-label">Creator payout (₹)</span>
              <input
                className="iv-input iv-num"
                type="number"
                min="0"
                value={form.influencer_final_price}
                onChange={set('influencer_final_price')}
              />
              {errors.influencer_final_price ? (
                <span className="iv-error">{errors.influencer_final_price}</span>
              ) : null}
            </label>
            <label className="iv-field">
              <span className="iv-label">Client billing (₹)</span>
              <input
                className="iv-input iv-num"
                type="number"
                min="0"
                value={form.final_price}
                onChange={set('final_price')}
              />
            </label>
          </div>
          <label className="iv-field">
            <span className="iv-label">Why is sign-in being skipped?</span>
            <input
              className="iv-input"
              value={form.reason}
              onChange={set('reason')}
              placeholder="e.g. Brand needs payment released today; creator's agency won't authenticate"
            />
            {errors.reason ? <span className="iv-error">{errors.reason}</span> : null}
          </label>
          <label className="iv-field">
            <span className="iv-label">Link expires in (days)</span>
            <input
              className="iv-input iv-num"
              type="number"
              min="1"
              value={form.expires_in_days}
              onChange={set('expires_in_days')}
            />
          </label>
        </>
      )}
    </Modal>
  );
}

function IssueLinkModal({ row, onClose, onIssued }) {
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('7');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    setReason('');
    setDays('7');
    setError('');
    setIssued(null);
  }, [row]);

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const link = await createExceptionLink({
        shortlisted_influencer_id: row.id,
        reason,
        expires_in_days: days,
      });
      setIssued(link);
      onIssued();
    } catch (e) {
      const fields = e.fieldErrors || {};
      setError(fields.reason || fields.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={Boolean(row)}
      onClose={saving ? () => {} : onClose}
      title="Exception invoice link"
      subtitle={row ? `${row.influencer_name} — invoice without signing in` : ''}
      footer={
        issued ? (
          <button type="button" className="iv-btn iv-btn--primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="iv-btn iv-btn--primary" onClick={create} disabled={saving}>
              {saving ? <span className="iv-spin" /> : null}
              Create link
            </button>
          </>
        )
      }
    >
      {issued ? (
        <div>
          <p className="ws-sub">
            Share this with the creator or their agency, or open it yourself to fill it in on their
            behalf. It works until {shortDate(issued.expires_at)} and can be revoked any time.
          </p>
          <div className="ws-url">
            <input className="iv-input" readOnly value={issued.url} onFocus={(e) => e.target.select()} />
            <button type="button" className="iv-btn iv-btn--primary iv-btn--sm" onClick={() => copy(issued.url)}>
              Copy
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          <Alert>
            This is the exception, not the default — creators should raise invoices themselves.
            Every link and its reason show up in the usage count on the invoice console.
          </Alert>
          <label className="iv-field">
            <span className="iv-label">Why this creator is paid outside the normal flow</span>
            <textarea
              className={`iv-textarea${error ? ' iv-textarea--invalid' : ''}`}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Brand needs payment released today; creator's agency won't authenticate"
            />
            {error ? <span className="iv-hint iv-hint--error">{String(error)}</span> : null}
          </label>
          <label className="iv-field">
            <span className="iv-label">Link valid for</span>
            <select className="iv-select" value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="1">1 day</option>
              <option value="3">3 days</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
            </select>
          </label>
        </div>
      )}
    </Modal>
  );
}

export default function InvoiceWorkspace() {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('ws-page', CSS);
  useStyleSheet('pc-commercials', COMMERCIALS_CSS);
  useStyleSheet('nt-notices', NOTICE_CSS);
  useResponsiveViewport();

  const [campaign, setCampaign] = useState(null);
  // The console links here with ?campaign_id=, so staff land on the campaign
  // they were already looking at rather than searching for it again.
  const [wantedId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('campaign_id') || '';
    } catch (e) {
      return '';
    }
  });
  const [creators, setCreators] = useState([]);
  const [links, setLinks] = useState([]);
  const [spend, setSpend] = useState(0);
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [campaignPosts, setCampaignPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [issuing, setIssuing] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [newCampaignOpen, setNewCampaignOpen] = useState(false);
  // Bumped after a campaign is created so the picker re-queries and the new
  // one is in the list behind the selection.
  const [campaignListKey, setCampaignListKey] = useState(0);
  const [exporting, setExporting] = useState('');

  const load = useCallback(async () => {
    if (!campaign) return;
    setLoading(true);
    setError('');
    try {
      const [rows, linkRows, invoices] = await Promise.all([
        getWorkspaceCampaignCreators(campaign.id),
        getExceptionLinks(campaign.id),
        // Spend is derived here from the same invoice list the console reads,
        // so the two figures cannot disagree.
        getInvoiceDashboard({ campaign_id: campaign.id, page_size: 200 }).catch(() => null),
      ]);
      setCreators(rows || []);
      setLinks(linkRows || []);
      const invoiceRowsForCampaign = (invoices && invoices.results) || [];
      setInvoiceRows(invoiceRowsForCampaign);
      setSpend(spendOf(invoiceRowsForCampaign));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [campaign]);

  /**
   * Put a withdrawn line back. No confirmation: unlike removal this is
   * additive and immediately undoable by withdrawing again, and the server
   * reuses the existing line rather than creating a second one.
   */
  const restoreLine = useCallback(
    async (row) => {
      const payload = restorePayload(row);
      if (!payload || !campaign) return;
      setRestoring(true);
      try {
        await addWorkspaceCreator(campaign.id, payload);
        toast.success('Line is back on the campaign');
        load();
      } catch (e) {
        toast.error((e && e.message) || 'Could not add the line back.');
      } finally {
        setRestoring(false);
      }
    },
    [campaign, load],
  );

  useEffect(() => {
    load();
  }, [load]);

  // The campaign's posts, pulled for this campaign. Separate from load()
  // because it is several requests and the roster should not wait on them;
  // keyed on the id so re-reading the campaign record does not refetch.
  const campaignId = campaign ? campaign.id : null;
  useEffect(() => {
    if (!campaignId) {
      setCampaignPosts([]);
      return undefined;
    }
    let alive = true;
    setCampaignPosts([]);
    loadCampaignPosts(campaignId).then((posts) => {
      if (alive) setCampaignPosts(posts);
    });
    return () => {
      alive = false;
    };
  }, [campaignId]);

  const postsForLine = postsByLine(creators, campaignPosts);

  // What is stopping this campaign getting paid, counted rather than scanned.
  const attention = [];
  if (campaign && !loading) {
    const noPayout = creators.filter(
      (row) => !isVendor(row) && row.payout_details_on_file === false,
    ).length;
    const unlocked = creators.filter((row) => !row.price_locked).length;
    const awaiting = creators.filter((row) => row.invoice_status === 'submitted').length;
    if (noPayout > 0) attention.push(`${noPayout} without payout details`);
    if (unlocked > 0) attention.push(`${unlocked} price${unlocked === 1 ? '' : 's'} not locked`);
    if (awaiting > 0) attention.push(`${awaiting} invoice${awaiting === 1 ? '' : 's'} to review`);
  }

  const runExport = async (kind) => {
    setExporting(kind);
    try {
      const filters = { campaign_id: campaign.id };
      const result = kind === 'zip' ? await downloadInvoiceZip(filters) : await downloadInvoiceXlsx(filters);
      if (kind === 'zip' && result && result.skipped) {
        toast(`${result.skipped} invoice(s) had no readable file and were left out.`);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting('');
    }
  };

  const revoke = async (link) => {
    try {
      await revokeExceptionLink(link.id);
      toast.success('Link revoked');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="iv-root iv-page">
      <div className="iv-wrap iv-wrap--console">
        <header className="iv-head">
          <div>
            <h1 className="iv-h1">Campaign workspace</h1>
            <p className="iv-lede">
              Add creators to a campaign, lock what they are paid and what the client is billed, and
              issue exception links when a creator can&apos;t invoice themselves.
            </p>
          </div>
          <a className="iv-btn iv-btn--ghost" href="/planner/invoices">
            Invoice console
            <Icon path={ICONS.arrow} size={14} />
          </a>
        </header>

        <NoticeStrip
          audience={AUDIENCE.BUSINESS}
          context={{
            invoices: invoiceRows,
            campaigns: campaign ? [campaign] : [],
            lines: creators,
          }}
        />

        <div className="ws-layout">
          <CampaignPicker
            selectedId={campaign && campaign.id}
            onSelect={setCampaign}
            refreshKey={campaignListKey}
            onNew={() => setNewCampaignOpen(true)}
            autoSelectId={wantedId}
          />

          <div className="ws-main">
            {!campaign ? (
              <div className="ws-card">
                <Empty icon={ICONS.search} title="Pick a campaign">
                  Search on the left to set up a campaign&apos;s creators and prices.
                </Empty>
              </div>
            ) : (
              <>
                <div className="ws-card">
                  <div className="ws-card__head" style={{ marginBottom: 0 }}>
                    <div>
                      <p className="ws-card__title">{campaign.name}</p>
                      <p className="ws-card__sub">
                        {campaign.brand_name ? `${campaign.brand_name} · ` : ''}
                        {campaign.campaign_subtype || 'campaign'} · {campaign.status || 'draft'}
                        {campaign.end_date ? ` · ends ${shortDate(campaign.end_date)}` : ''}
                      </p>
                    </div>
                    <div className="ws-actions">
                      <button
                        type="button"
                        className="iv-btn iv-btn--ghost"
                        disabled={Boolean(exporting)}
                        onClick={() => runExport('xlsx')}
                      >
                        {exporting === 'xlsx' ? <span className="iv-spin" /> : <Icon path={ICONS.sheet} size={15} />}
                        Spreadsheet
                      </button>
                      <button
                        type="button"
                        className="iv-btn iv-btn--ghost"
                        disabled={Boolean(exporting)}
                        onClick={() => runExport('zip')}
                      >
                        {exporting === 'zip' ? <span className="iv-spin" /> : <Icon path={ICONS.download} size={15} />}
                        Invoice files
                      </button>
                      <button
                        type="button"
                        className="iv-btn iv-btn--ghost"
                        onClick={() => setQuickOpen(true)}
                        title="Add a creator and issue their no-sign-in link in one step"
                      >
                        Urgent link
                      </button>
                      <a className="iv-btn iv-btn--ghost" href={`/planner/invoices?campaign_id=${campaign.id}`}>
                        Invoices
                        <Icon path={ICONS.arrow} size={14} />
                      </a>
                    </div>
                  </div>
                </div>

                <ProjectCommercials
                  campaign={campaign}
                  spend={spend}
                  lines={creators}
                  invoices={invoiceRows}
                  onSaved={(saved) => setCampaign((current) => ({ ...current, ...(saved || {}) }))}
                />

                {attention.length > 0 ? (
                  <div className="ws-attention" role="status">
                    <span className="ws-attention__label">Needs attention</span>
                    {attention.map((item) => (
                      <span className="ws-attention__item" key={item}>
                        {item}
                      </span>
                    ))}
                  </div>
                ) : null}

                <AddCreator campaignId={campaign.id} onAdded={load} />

                {error ? <Alert onRetry={load}>{error}</Alert> : null}

                <div className="ws-card">
                  <div className="ws-card__head">
                    <div>
                      <p className="ws-card__title">Creators on this campaign</p>
                      <p className="ws-card__sub">
                        A locked price shows on the creator&apos;s screen and can only be changed here.
                      </p>
                    </div>
                  </div>
                  {loading && creators.length === 0 ? (
                    <div className="iv-sk" style={{ height: 120, borderRadius: 12 }} />
                  ) : creators.length === 0 ? (
                    <Empty title="No creators yet">Add one above.</Empty>
                  ) : (
                    <div className="ws-scroll">
                      <table className="ws-table">
                        <thead>
                          <tr>
                            <th>Creator</th>
                            <th>Payout (₹)</th>
                            <th>Client billing (₹)</th>
                            <th>Price</th>
                            <th>Bank details</th>
                            <th>Invoice</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {creators.map((row) => (
                            <CreatorRow
                              key={row.id}
                              row={row}
                              invoice={matchInvoice(row, invoiceRows)}
                              campaignInvoices={invoiceRows}
                              campaignPosts={postsForLine[row.id] || []}
                              link={links.find(
                                (each) => each.shortlisted_influencer_id === row.id,
                              )}
                              onSaved={load}
                              onIssueLink={setIssuing}
                              onInvite={setInviting}
                              onRemove={setRemoving}
                              onRestore={restoreLine}
                              restoring={restoring}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="ws-card">
                  <div className="ws-card__head">
                    <div>
                      <p className="ws-card__title">Exception links</p>
                      <p className="ws-card__sub">Every no-sign-in link issued for this campaign, and why.</p>
                    </div>
                  </div>
                  {links.length === 0 ? (
                    <p className="ws-sub">None issued — good.</p>
                  ) : (
                    <div className="ws-scroll">
                      <table className="ws-table">
                        <thead>
                          <tr>
                            <th>Creator</th>
                            <th>Reason</th>
                            <th>Issued</th>
                            <th>State</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {links.map((link) => {
                            const state = linkState(link);
                            return (
                              <tr key={link.id}>
                                <td className="ws-name">{link.influencer_name}</td>
                                <td style={{ maxWidth: 320 }}>{link.reason}</td>
                                <td>
                                  <div>{shortDate(link.created_at)}</div>
                                  <div className="ws-sub">{link.created_by}</div>
                                </td>
                                <td>
                                  <span className={`ws-tag ws-tag--${state.tone}`}>{state.label}</span>
                                  {link.invoice_status ? (
                                    <div style={{ marginTop: 4 }}>
                                      <StatusPill status={link.invoice_status} />
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  <div className="ws-actions" style={{ justifyContent: 'flex-end' }}>
                                    {link.is_active ? (
                                      <>
                                        <button type="button" className="iv-btn iv-btn--ghost iv-btn--sm" onClick={() => copy(link.url)}>
                                          Copy
                                        </button>
                                        <button type="button" className="iv-btn iv-btn--quiet iv-btn--sm" onClick={() => revoke(link)}>
                                          Revoke
                                        </button>
                                      </>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <IssueLinkModal row={issuing} onClose={() => setIssuing(null)} onIssued={load} />
      <InviteModal row={inviting} campaign={campaign} onClose={() => setInviting(null)} />
      {removing ? (
        <RemoveLineModal
          row={removing}
          campaignId={campaign ? campaign.id : null}
          onClose={() => setRemoving(null)}
          onDone={load}
        />
      ) : null}
      <NewCampaignModal
        open={newCampaignOpen}
        onClose={() => setNewCampaignOpen(false)}
        onCreated={(created) => {
          setCampaignListKey((k) => k + 1);
          // Land straight on the new campaign, ready for its creators.
          setCampaign(created);
        }}
      />
      <QuickInviteModal
        open={quickOpen}
        campaign={campaign}
        onClose={() => setQuickOpen(false)}
        onDone={load}
      />
      <Toaster position="bottom-center" />
    </div>
  );
}

